'use client';

import type { OnChangeFn, RowSelectionState, Updater } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

interface UseTableSelectionOptions<TData> {
  /** Current page of rows; used to hydrate and refresh the object cache */
  data: TData[];
  /** Stable row identity */
  getRowId: (row: TData) => string;
}

export interface TableSelection<TData> {
  /** Full selected row objects, hydrated from the internal cache; survives pagination */
  rowSelectionFullObject: TData[];
  /** Controlled selection state consumed by DataTable */
  rowSelection: RowSelectionState;
  /** Controlled selection change handler consumed by DataTable */
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  /** Stable row identity function consumed by DataTable */
  getRowId: (row: TData) => string;
  /** Replaces the entire selection with the given rows (e.g. select-all) */
  selectRows: (rows: TData[]) => void;
  /** Clears the current selection */
  clearSelection: () => void;
}

interface TableSelectionState<TData> {
  rowSelection: RowSelectionState;
  rowSelectionFullObjectById: Map<string, TData>;
}

interface ChangeSelectionAction<TData> {
  type: 'change';
  updater: Updater<RowSelectionState>;
  visibleRowsById: Map<string, TData>;
}

interface ClearSelectionAction {
  type: 'clear';
}

interface SyncVisibleRowsAction<TData> {
  type: 'sync-visible-rows';
  visibleRowsById: Map<string, TData>;
}

interface SelectRowsAction<TData> {
  type: 'select-rows';
  rowsById: Map<string, TData>;
}

type TableSelectionAction<TData> =
  | ChangeSelectionAction<TData>
  | ClearSelectionAction
  | SyncVisibleRowsAction<TData>
  | SelectRowsAction<TData>;

function createInitialState<TData>(): TableSelectionState<TData> {
  return { rowSelection: {}, rowSelectionFullObjectById: new Map<string, TData>() };
}

function tableSelectionReducer<TData>(
  state: TableSelectionState<TData>,
  action: TableSelectionAction<TData>
): TableSelectionState<TData> {
  if (action.type === 'clear') {
    return createInitialState<TData>();
  }

  // Replace the entire selection with the given rows.
  if (action.type === 'select-rows') {
    const rowSelection: RowSelectionState = {};
    action.rowsById.forEach((_row, id) => {
      rowSelection[id] = true;
    });
    return { rowSelection, rowSelectionFullObjectById: new Map(action.rowsById) };
  }

  // Refresh cached objects for currently-selected rows that are visible on the
  // current page; off-page selections are left untouched.
  if (action.type === 'sync-visible-rows') {
    if (state.rowSelectionFullObjectById.size === 0) return state;

    const rowSelectionFullObjectById = new Map(state.rowSelectionFullObjectById);
    let hasChanged = false;

    action.visibleRowsById.forEach((row, id) => {
      if (!state.rowSelection[id] || rowSelectionFullObjectById.get(id) === row) return;
      rowSelectionFullObjectById.set(id, row);
      hasChanged = true;
    });

    return hasChanged ? { ...state, rowSelectionFullObjectById } : state;
  }

  const rowSelection =
    typeof action.updater === 'function' ? action.updater(state.rowSelection) : action.updater;

  // Rebuild in a stable order. Do not iterate Object.entries(rowSelection): integer-like
  // string ids are sorted ascending by the engine, which reverses newest-first tables.
  const rowSelectionFullObjectById = new Map<string, TData>();

  // Keep previously selected rows that remain selected (preserves off-page order).
  state.rowSelectionFullObjectById.forEach((row, id) => {
    if (!rowSelection[id]) return;
    const visibleRow = action.visibleRowsById.get(id);
    rowSelectionFullObjectById.set(id, visibleRow ?? row);
  });

  // Append newly selected visible rows in current-page / data order.
  action.visibleRowsById.forEach((row, id) => {
    if (!rowSelection[id] || rowSelectionFullObjectById.has(id)) return;
    rowSelectionFullObjectById.set(id, row);
  });

  return { rowSelection, rowSelectionFullObjectById };
}

/**
 * Lifts TanStack Table row selection into the consumer with stable, id-based
 * identity. Selection is keyed by getRowId so it stays
 * correct across refetch/sort/filter, and persists across pagination. Full row
 * objects are cached internally so rowSelectionFullObject stays populated even for rows
 * that are no longer on the current page.
 */
export function useTableSelection<TData>({
  data,
  getRowId,
}: UseTableSelectionOptions<TData>): TableSelection<TData> {
  // Consumers usually pass getRowId as an inline arrow, so its reference changes
  // every render. Depending on it directly would rebuild visibleRowsById on every
  // render, which then cascades into onRowSelectionChange and the sync-visible-rows
  // effect. Hold the latest fn in a ref and expose a stable wrapper so
  // visibleRowsById only recomputes when data actually changes.
  const getRowIdRef = useRef(getRowId);

  useEffect(() => {
    getRowIdRef.current = getRowId;
  }, [getRowId]);

  const stableGetRowId = useCallback((row: TData) => getRowIdRef.current(row), []);

  const visibleRowsById = useMemo(
    () => new Map(data.map((row) => [stableGetRowId(row), row] as const)),
    [data, stableGetRowId]
  );

  const [state, dispatch] = useReducer(
    tableSelectionReducer<TData>,
    undefined,
    createInitialState<TData>
  );

  useEffect(() => {
    dispatch({ type: 'sync-visible-rows', visibleRowsById });
  }, [visibleRowsById]);

  const onRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => dispatch({ type: 'change', updater, visibleRowsById }),
    [visibleRowsById]
  );

  const clearSelection = useCallback(() => {
    dispatch({ type: 'clear' });
  }, []);

  const selectRows = useCallback(
    (rows: TData[]) => {
      const rowsById = new Map(rows.map((row) => [stableGetRowId(row), row] as const));
      dispatch({ type: 'select-rows', rowsById });
    },
    [stableGetRowId]
  );

  const rowSelectionFullObject = useMemo(
    () => Array.from(state.rowSelectionFullObjectById.values()),
    [state.rowSelectionFullObjectById]
  );

  return {
    rowSelectionFullObject,
    rowSelection: state.rowSelection,
    clearSelection,
    selectRows,
    onRowSelectionChange,
    getRowId: stableGetRowId,
  };
}
